// Read-only rule predicates and derived values. Validation, legalActions, and
// the UI all build on these so they can't drift apart.

import { TERRAIN_RESOURCE, type Board } from './board';
import { RESOURCES, hasResources, totalResources, type Resource, type Resources } from './resources';
import { otherEnd, topologyOf, type Topology } from './topology';
import type { DevCardType, GameState, HouseRules, PlayerState, ProgressCard } from './types';

export const PIECES = { roads: 15, settlements: 5, cities: 4 } as const;
export const MIN_LONGEST_ROAD = 5;
export const MIN_LARGEST_ARMY = 3;
export const DISCARD_THRESHOLD = 7;

/**
 * Rules that apply when a game doesn't say otherwise. The robber and Victory
 * Point rules were always on before they became toggles, so older games keep them.
 */
export const DEFAULT_HOUSE_RULES: HouseRules = {
  robberWaits: true,
  revealVictoryPoints: true,
  islands: false,
  scrambleNumbers: false,
  specialBuild: false,
};

/** The fewest players for building between turns to apply. */
export const SPECIAL_BUILD_MIN_PLAYERS = 5;

export function specialBuildOn(state: GameState): boolean {
  return !!houseRules(state).specialBuild && state.config.playerCount >= SPECIAL_BUILD_MIN_PLAYERS;
}

export function houseRules(state: { config: { rules?: Partial<HouseRules> } }): HouseRules {
  return { ...DEFAULT_HOUSE_RULES, ...state.config.rules };
}

/** Development deck: the base 25 cards up to 4 players, the extension's 34 for 5-6, and 9 more per two players after that. */
export function devDeckFor(playerCount: number): DevCardType[] {
  const extra = playerCount <= 4 ? 0 : 1 + Math.ceil(Math.max(0, playerCount - 6) / 2);
  return [
    ...Array<DevCardType>(14 + 6 * extra).fill('knight'),
    ...Array<DevCardType>(5).fill('victoryPoint'),
    ...Array<DevCardType>(2 + extra).fill('roadBuilding'),
    ...Array<DevCardType>(2 + extra).fill('yearOfPlenty'),
    ...Array<DevCardType>(2 + extra).fill('monopoly'),
  ];
}

export const DEV_DECK: readonly DevCardType[] = devDeckFor(4);

/** Bank cards per resource: 19 up to 4 players, 24 for 5-6, and 5 more per two players after that. */
export function bankSizeFor(playerCount: number): number {
  if (playerCount <= 4) return 19;
  return 24 + 5 * Math.ceil(Math.max(0, playerCount - 6) / 2);
}

export function isIndex(x: unknown, count: number): x is number {
  return typeof x === 'number' && Number.isInteger(x) && x >= 0 && x < count;
}

export const isVertex = (t: Topology, x: unknown): x is number => isIndex(x, t.vertices.length);
export const isEdge = (t: Topology, x: unknown): x is number => isIndex(x, t.edges.length);
export const isHex = (t: Topology, x: unknown): x is number => isIndex(x, t.hexes.length);

export function knightsPlayed(p: PlayerState): number {
  return p.playedDevCards.filter((c) => c === 'knight').length;
}

/**
 * House rule: the robber sits out until someone builds past their setup
 * pieces (a third settlement or any city). Until then 7s are re-rolled and
 * Knights can't be played. Buildings never go away, so once true it stays true.
 */
export function robberActive(state: GameState): boolean {
  if (!houseRules(state).robberWaits) return true;
  const settlements: number[] = [];
  for (const b of state.buildings) {
    if (!b) continue;
    if (b.kind === 'city') return true;
    const n = (settlements[b.owner] ?? 0) + 1;
    if (n > 2) return true;
    settlements[b.owner] = n;
  }
  return false;
}

export function victoryPoints(state: GameState, player: number): number {
  let vp = 0;
  for (const b of state.buildings) if (b?.owner === player) vp += b.kind === 'city' ? 2 : 1;
  if (state.longestRoad === player) vp += 2;
  if (state.largestArmy === player) vp += 2;
  vp += state.players[player]!.devCards.filter((c) => c.type === 'victoryPoint').length;
  return vp;
}

/** Points everyone can see: hidden Victory Point cards are left out unless the reveal rule is on. */
export function publicVictoryPoints(state: GameState, player: number): number {
  if (houseRules(state).revealVictoryPoints) return victoryPoints(state, player);
  return victoryPoints(state, player) - state.players[player]!.devCards.filter((c) => c.type === 'victoryPoint').length;
}

export function canPlaceSettlement(state: GameState, player: number, vertex: number, setup: boolean): boolean {
  const t = topologyOf(state);
  if (!isVertex(t, vertex) || state.buildings[vertex] !== null) return false;
  const v = t.vertices[vertex]!;
  if (v.neighbors.some((n) => state.buildings[n] !== null)) return false;
  if (setup) return true;
  return v.edges.some((e) => state.roads[e] === player);
}

/**
 * A road connects through an endpoint the player owns a building on, or an
 * endpoint with another of their roads that isn't occupied by an opponent.
 * During setup the road must touch `anchor` (the settlement just placed).
 */
export function canPlaceRoad(state: GameState, player: number, edge: number, anchor?: number): boolean {
  const t = topologyOf(state);
  if (!isEdge(t, edge) || state.roads[edge] !== null) return false;
  const ends = t.edges[edge]!.vertices;
  if (anchor !== undefined) return ends.includes(anchor);
  return ends.some((v) => {
    const b = state.buildings[v];
    if (b) return b.owner === player;
    return t.vertices[v]!.edges.some((e) => e !== edge && state.roads[e] === player);
  });
}

export function canPlaceCity(state: GameState, player: number, vertex: number): boolean {
  if (!isVertex(topologyOf(state), vertex)) return false;
  const b = state.buildings[vertex];
  return b !== null && b !== undefined && b.owner === player && b.kind === 'settlement';
}

export function legalRoadEdges(state: GameState, player: number): number[] {
  const out: number[] = [];
  const edges = topologyOf(state).edges.length;
  for (let e = 0; e < edges; e++) if (canPlaceRoad(state, player, e)) out.push(e);
  return out;
}

export function canAfford(state: GameState, player: number, cost: Resources): boolean {
  return hasResources(state.players[player]!.resources, cost);
}

/** Best maritime rate for giving `resource`: 2 with its harbor, 3 with a generic harbor, else 4. */
export function bankRate(state: Pick<GameState, 'board' | 'buildings'>, player: number, resource: Resource): number {
  let rate = 4;
  const t = topologyOf(state);
  for (const port of state.board.ports) {
    const owns = t.edges[port.edge]!.vertices.some((v) => state.buildings[v]?.owner === player);
    if (!owns) continue;
    if (port.type === resource) return 2;
    if (port.type === 'any') rate = 3;
  }
  return rate;
}

/**
 * A starting point for a discard of `owed` cards: half of each pile (rounded
 * down), then the rest one at a time from whichever pile has the most left.
 */
export function suggestDiscard(hand: Resources, owed: number): Resources {
  const out = { brick: 0, lumber: 0, wool: 0, grain: 0, ore: 0 };
  let left = owed;
  for (const r of RESOURCES) {
    out[r] = Math.min(Math.floor(hand[r] / 2), left);
    left -= out[r];
  }
  while (left > 0) {
    let best: Resource | null = null;
    for (const r of RESOURCES) if (hand[r] - out[r] > 0 && (!best || hand[r] - out[r] > hand[best] - out[best])) best = r;
    if (!best) break;
    out[best]++;
    left--;
  }
  return out;
}

/** Identifies one turn (or one setup placement) for turn timers: it changes exactly when a timer should restart. */
export function turnKey(state: Pick<GameState, 'turn' | 'currentPlayer' | 'phase'>): string {
  return `${state.turn}:${state.currentPlayer}:${state.phase.kind === 'setup' ? state.phase.index : ''}`;
}

export function discardOwed(state: GameState, player: number): number {
  return state.phase.kind === 'discard' ? (state.phase.owed[player] ?? 0) : 0;
}

export function stealCandidates(state: GameState, thief: number, hex: number): number[] {
  const out: number[] = [];
  for (const v of topologyOf(state).hexVertices[hex]!) {
    const owner = state.buildings[v]?.owner;
    if (owner === undefined || owner === thief || out.includes(owner)) continue;
    if (totalResources(state.players[owner]!.resources) > 0) out.push(owner);
  }
  return out.sort((a, b) => a - b);
}

/** Resources each player collects for a roll, before bank-shortage rules. */
export function productionFor(state: GameState, total: number): Resources[] {
  const gains = state.players.map(() => ({ brick: 0, lumber: 0, wool: 0, grain: 0, ore: 0 }));
  const t = topologyOf(state);
  state.board.tiles.forEach((tile, hex) => {
    if (tile.token !== total || hex === state.robber) return;
    const resource = TERRAIN_RESOURCE[tile.terrain];
    if (!resource) return;
    for (const v of t.hexVertices[hex]!) {
      const b = state.buildings[v];
      if (b) gains[b.owner]![resource] += b.kind === 'city' ? 2 : 1;
    }
  });
  return gains;
}

/** A playable copy of `type`: bought before this turn, and no dev card played yet this turn. */
export function playableDevCardIndex(state: GameState, player: number, type: ProgressCard): number {
  const p = state.players[player]!;
  if (p.playedDevCardThisTurn) return -1;
  return p.devCards.findIndex((c) => c.type === type && c.boughtOnTurn < state.turn);
}

/**
 * Longest trail through the player's roads: each road used at most once,
 * vertices may repeat (loops count), and an opponent's building on a vertex
 * stops the trail there.
 */
export function longestRoadLength(state: Pick<GameState, 'roads' | 'buildings'> & { board: Board }, player: number): number {
  const t = topologyOf(state);
  const used = new Uint8Array(t.edges.length);
  let best = 0;

  const walk = (vertex: number, length: number): void => {
    if (length > best) best = length;
    const b = state.buildings[vertex];
    if (length > 0 && b && b.owner !== player) return;
    for (const e of t.vertices[vertex]!.edges) {
      if (used[e] || state.roads[e] !== player) continue;
      used[e] = 1;
      walk(otherEnd(e, vertex, t), length + 1);
      used[e] = 0;
    }
  };

  for (let v = 0; v < t.vertices.length; v++) {
    if (t.vertices[v]!.edges.some((e) => state.roads[e] === player)) walk(v, 0);
  }
  return best;
}

/**
 * Who should hold Longest Road given the current board. The holder keeps it
 * while tied for longest; otherwise a unique leader with 5+ takes it, and a
 * tie with no holder among the leaders leaves it unclaimed.
 */
export function longestRoadHolder(state: GameState): number | null {
  const lengths = state.players.map((_, i) => longestRoadLength(state, i));
  const max = Math.max(...lengths);
  if (max < MIN_LONGEST_ROAD) return null;
  const holder = state.longestRoad;
  if (holder !== null && lengths[holder] === max) return holder;
  const leaders = lengths.flatMap((len, i) => (len === max ? [i] : []));
  return leaders.length === 1 ? leaders[0]! : null;
}

/** Snake order for setup, starting from `first` (who won the roll for first). */
export function setupOrder(playerCount: number, first = 0): number[] {
  const forward = Array.from({ length: playerCount }, (_, i) => (first + i) % playerCount);
  return [...forward, ...forward.slice().reverse()];
}

export function tradeIsWellFormed(give: Resources, want: Resources): boolean {
  if (totalResources(give) === 0 || totalResources(want) === 0) return false;
  return RESOURCES.every((r) => give[r] === 0 || want[r] === 0);
}
